import React, { useMemo } from 'react';
import { RichTextBlock } from 'prismic-reactjs';
import { ImageProps } from '@components/image';
import Text from '@components/text';
import getLinkConfig from '@helpers/get-link-config';
import NotificationBanner from '@components/notification-banner';
import Wrapper from '@components/wrapper.styled';
import AnimatedElement from '@components/animated-element';
import {
  Content,
  Header,
  LinkButton,
  StyledImage,
  TextContent,
  StyledWrapper,
  ImageWrapper,
} from './illustration-hero.styled';

export interface LinkProps {
  url?: string;
  uid?: string;
  target?: string;
}

export interface NotificationProps {
  url?: string;
  target?: string;
}

export interface IllustrationHeroProps {
  image?: ImageProps;
  title?: string;
  subheading?: RichTextBlock[];
  linkLabel?: string;
  link?: LinkProps;
  notificationMessage?: string;
  notificationIcon?: ImageProps;
  notificationUrl?: NotificationProps;
  isNotificationDismissible?: boolean;
}

const DEFAULT_LINK_LABEL = 'Request a Demo';
const TITLE_LENGTH_THRESHOLD = 56;

const IllustrationHero: React.FC<IllustrationHeroProps> = ({
  image,
  title,
  subheading = [],
  linkLabel,
  link,
  notificationMessage,
  notificationIcon,
  notificationUrl,
  isNotificationDismissible = true,
}) => {
  if (!title && !image && !subheading.length) {
    return null;
  }

  const linkConfig = useMemo(() => getLinkConfig(link), [link]);
  const hasImage = useMemo(() => !!(image?.gatsbyImageData || image?.url), [image]);

  const titleSize = useMemo(
    () => (title && title.length >= TITLE_LENGTH_THRESHOLD ? 'heading2' : 'heading1'),
    [title]
  );

  const hasUrl = useMemo(
    () => notificationUrl?.url && notificationUrl.url.length > 0,
    [notificationUrl?.url]
  );

  const TextBlocks = useMemo(
    () => (
      <TextContent>
        {title && (
          <Text
            fontSize={titleSize}
            type="title"
            tag="h1"
            stringText={title}
            fontColor="white"
            margin="none"
          />
        )}

        {subheading.length > 0 && (
          <Text type="subheading" richText={subheading} fontColor="black" margin="none" />
        )}
      </TextContent>
    ),
    [title, titleSize, subheading]
  );

  return (
    <Header className="page-hero hero-primary" role="banner" aria-label={title || 'Page Hero'}>
      <Wrapper>
        <StyledWrapper>
          <Content image={image}>
            {hasUrl && (
              <NotificationBanner
                notificationMessage={notificationMessage}
                notificationIcon={notificationIcon}
                notificationUrl={notificationUrl || {}}
                isNotificationDismissible={isNotificationDismissible}
              />
            )}

            {TextBlocks}

            {linkConfig.url !== '' && (
              <LinkButton
                type="button"
                url={linkConfig.url}
                title={linkLabel || DEFAULT_LINK_LABEL}
                isExternal={linkConfig.isExternal}
                target={link?.target || '_self'}
                aria-label={linkLabel || DEFAULT_LINK_LABEL}
              />
            )}
          </Content>

          <ImageWrapper>
            {hasImage && (
              <AnimatedElement
                animationType="slideInRight"
                delay={0}
                threshold={0.1}
                rootMargin="50px"
              >
                <StyledImage
                  gatsbyImageData={image?.gatsbyImageData}
                  url={image?.url}
                  alt={image?.alt || title || 'Hero image'}
                  objectFit="contain"
                  loading="eager"
                />
              </AnimatedElement>
            )}
          </ImageWrapper>
        </StyledWrapper>
      </Wrapper>
    </Header>
  );
};

IllustrationHero.defaultProps = {
  isNotificationDismissible: true,
  subheading: [],
};

export default React.memo(IllustrationHero);
